import XlsDownload from "@dana/Icons/XlsDownload";
import { elevation, themeColors } from "@dana/MobiusOverrides/styles/colors";
import Icon from "@insite/mobius/Icon";
import * as XLSX from "xlsx-js-style";
import React, { forwardRef } from "react";
import styled from "styled-components";
import { InvoiceDetailsResponse } from "@dana/Types/ApiModels";
import { formatDateWithDash } from "@dana/Common/utils";
import translate from "@insite/client-framework/Translate";
import { formatDecimal } from "@dana/utils/utils";

type NACommercialExcelDownloadButtonProps = {
    invoice?: InvoiceDetailsResponse | null;
    totalAmt?:number;    
    totalDiscount?:number;   
    totalPrice?:number;
    weightUnit?:string;
    totalWeight?:number;
    miscCharges?:number;
    totalTariff?:number;
    fileName?: string;
    sheetName?: string;
    label?: string;
    size?: number;
    disabled?: boolean;
    className?: string;
    selectedRegion?:string;
    onBeforeDownload?: () => void | Promise<void>;
    onAfterDownload?: () => void;
};

const StyledExcelDownloadButton = styled.button`
    padding: 4px;
    background-color: transparent;
    border: none;
    cursor: pointer;

    &:hover {
        color: ${themeColors.secondary.danaDarkBlue};
    }

    &:focus-visible {
        color: ${themeColors.secondary.danaDarkBlue};
        border: 1px solid ${themeColors.primary.white};
        box-shadow: ${elevation.focus};
    }

    @media print {
        display: none;
    }
`;

const NUM_FMT = "0.00";

function applyAlignment(ws: XLSX.WorkSheet, cellAddress: string, horizontal: "right" | "left" | "center" = "right") {
    if (ws[cellAddress]) {
        ws[cellAddress].s = {
            ...(ws[cellAddress].s ?? {}),
            alignment: { horizontal },
        };
    }
}

function applyNumberFormat(ws: XLSX.WorkSheet, cellAddress: string) {
    if (ws[cellAddress]) {
        ws[cellAddress].z = NUM_FMT;
        applyAlignment(ws, cellAddress, "right");
    }
}

function buildInvoiceWorkbook(invoice: InvoiceDetailsResponse | null, 
    totalAmt:number,
    totalDiscount:number,    
    totalPrice:number,
    weightUnit:string,
    totalWeight: number,
    miscCharges:number,
    totalTariff:number,
    fileName: string, sheetName: string, selectedRegion:string) {
    const rows: (string | number | null)[][] = [
        [translate("CUSTOMER"), invoice?.mailName ??""],
        [translate("PURCHASE ORDER NO."), invoice?.poNumber ?? ""],
        [translate("ORDER NUMBER"), invoice?.documentNumber ?? ""],
        [translate("INVOICE NO."), invoice?.invoiceNumber ?? ""],
        [translate("DATE OF EXPORT"), invoice?.documentDate?.split('T')[0] || ""],
        [translate("TERMS OF PAYMENT"), invoice?.termsDescription ??""],
        [translate("B/L, AWB NO."), invoice?.deliveryNumber ??""],
        [translate("FEDERAL TAX ID"),"26-1346950-01"],
        [],
        [],
        [
            translate("Item"),
            translate("Dana Part Number"),
            translate("Customer Part Number"),
            translate("Part Description"),
            translate("Country of Origin"),
            translate("H.S. Number"),
            `${translate("Item Weight")} (${(weightUnit)})`,
             translate("Quantity"),
            `${translate("Unit Price In")} ${invoice?.currency ?? ""}`,          
            `${translate("Value In")} ${invoice?.currency ?? ""}`,
        ],
       ...(invoice?.invoiceInfo?.map(item => [
        item.lineNumber,
    item.partNumber,
    item.customerPartNumber,
    item.partDescription,
    item.countryOfOrigin,
    item.hsNumber,
     formatDecimal(item.weight),
     item.quantityOrdered,
     formatDecimal(item.pricePerUnit),  
     formatDecimal(item.netAmount),
]) ?? []),
        [],
        [null, null, null, null, null, null, null, null, `${translate("Total Weight")} (${weightUnit ?? ""}):`, totalWeight.toFixed(2)],
        [null, null, null, null, null, null, null, null,`${translate("Line Total")} (${invoice?.currency ?? ""}):`, formatDecimal(totalPrice)],       
        [null, null, null, null, null, null, null, null, `${translate("MISC CHARGES (Packing, Freight, etc.)")} (${invoice?.currency ?? ""}):`,  formatDecimal(miscCharges ?? 0)],
        [null, null, null, null, null, null, null, null, `${translate("Tariff Charges")} (${invoice?.currency ?? ""}):`, formatDecimal(totalTariff ??0)],        
        [null, null, null, null, null, null, null, null, `${translate("Discount")} (${invoice?.currency ?? ""}):`,  `-${formatDecimal(totalDiscount)}`],       
        [null, null, null, null, null, null, null, null, `${translate("Invoice Total")} (${invoice?.currency ?? ""}):`, formatDecimal(totalAmt)],
    ];

    const ws = XLSX.utils.aoa_to_sheet(rows);

   ws["!cols"] = [
    { wch: 18 },  
    { wch: 18 },  
    { wch: 20 },  
    { wch: 30 },  
    { wch: 18 },  
    { wch: 14 },  
    { wch: 16 },  
    { wch: 16 },  
    { wch: 28 }, 
    { wch: 16 },  
];
   
    const lineItemStartRow = 12; // row 10 = first data row (1-indexed, after 9 header rows)
    invoice?.invoiceInfo?.forEach((_, i) => {
        const rowNum = lineItemStartRow + i;
        applyNumberFormat(ws, `G${rowNum}`);       
        applyNumberFormat(ws, `I${rowNum}`);
        applyNumberFormat(ws, `J${rowNum}`);        
    });

    // Totals block numeric column (G), 5 rows starting after the blank spacer row
    const totalsStartRow = lineItemStartRow + (invoice?.invoiceInfo?.length ?? 0) + 1;
    for (let i = 0; i < 8; i++) {
        applyNumberFormat(ws, `J${totalsStartRow + i}`);        
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, `${fileName}.xlsx`);
}

export const NACommercialExcelDownloadButton = forwardRef<HTMLButtonElement, NACommercialExcelDownloadButtonProps>(
    (
        {
            invoice,
            totalAmt,           
            totalDiscount,
            totalPrice,
            weightUnit,
            totalWeight,
            miscCharges,
            totalTariff,
            fileName = "na commercial invoice",
            sheetName = "na Commercial Invoice",
            label = "Download Excel",
            size = 28,
            disabled,
            className,
            selectedRegion,
            onBeforeDownload,
            onAfterDownload,
        },
        ref,
    ) => {
        const handleDownload = async () => {
            if (disabled) return;
            if (typeof window === "undefined") return;

            try {
                await onBeforeDownload?.();
            } catch {
                // Swallow to avoid blocking download
            }

            try {
                buildInvoiceWorkbook(invoice ?? null,totalAmt ??0,totalDiscount??0,totalPrice??0, weightUnit??"", totalWeight??0,miscCharges??0, totalTariff??0,fileName, sheetName,selectedRegion ??"");
            } finally {
                onAfterDownload?.();
            }
        };

        return (
            <StyledExcelDownloadButton
                ref={ref}
                type="button"
                aria-label={label}
                title={label}
                onClick={handleDownload}
                className={className}
                disabled={disabled}
            >
                <Icon src={XlsDownload} size={size} />
            </StyledExcelDownloadButton>
        );
    },
);

NACommercialExcelDownloadButton.displayName = "NACommercialExcelDownloadButton";